import React, { useState } from 'react';
import { LuReply } from 'react-icons/lu';
import { Button } from '@/components/ui/button';
import { formatContent, formatExcelContent } from '../../services/ai/fastApiBackend';
import { formatOutlookBody } from '../../utils/textProcessing';
import { getEmailBody, displayReplyForm } from '../../services/office/outlook';
import { insertIntoWordDocument } from '../../services/office/word';
import { insertIntoPowerPointSlide } from '../../services/office/powerpoint';
import {
  insertIntoExcel,
  getExcelMetadata,
  startCellOf,
  parseBackendTableJson,
  insertJsonTableIntoExcel,
} from '../../services/office/excel';
import { HostType } from '../../types/office';
import { getUserIdentity } from '../../services/identity';
import { ModelId } from '../../types';
import { logger } from '../../utils/logger';

interface InsertButtonProps {
  mailContent: string;
  item: Office.MessageRead | Office.MessageCompose | null;
  disabled?: boolean;
  setIsInserting: (isInserting: boolean) => void;
  model: ModelId;
}

export const InsertButton: React.FC<InsertButtonProps> = ({ 
  mailContent, 
  item, 
  disabled = false,
  setIsInserting,
  model
}) => {
  const [generating, setGenerating] = useState(false);

  const handleInsert = async () => {
    const host = Office.context.host as HostType;
    
    setGenerating(true);
    setIsInserting(true);
    try {
      // Get access token from MSAL (allow auto-popup for user-initiated actions)
      const identityResult = await getUserIdentity(true);
      if (!identityResult.identity?.rawAccessToken) {
        throw new Error("Failed to get access token for authentication");
      }
      const identity = identityResult.identity;

      // Handle Word context
      if (host === "Word") {
        try {
          // Convert markdown content to HTML using FastAPI backend
          const rawHtmlContent = await formatContent(
            identity.rawAccessToken, // Access token from MSAL
            mailContent,
            "Word",
            "Convert markdown to HTML for Word document",
            model
          );

          const cleanedHtmlContent = rawHtmlContent;
          logger.debug("[InsertButton] Word HTML from formatContent, length:", cleanedHtmlContent.length);

          await insertIntoWordDocument(cleanedHtmlContent, true);
        } catch (err) {
          logger.error("Failed to insert into Word document:", err);
        }
        return;
      }

      // Handle PowerPoint context
      if (host === "PowerPoint") {
        try {
          // Insert content into PowerPoint slides
          // The content should already be in the format: ### Slide X: Title followed by bullet points
          await insertIntoPowerPointSlide(mailContent);
        } catch (err) {
          logger.error("Failed to insert into PowerPoint:", err);
        }
        return;
      }

      // Handle Excel context
      if (host === "Excel") {
        try {
          // Resolve the insertion target (selected range + active sheet).
          const { range, activeWorksheet } = await getExcelMetadata();
          const selectedCell = startCellOf(range);

          let tableData: string[][] | any[][] | null = null;

          // Preferred path: ask the backend to extract a structured table
          // (JSON array-of-arrays) with formulas anchored to the selected cell.
          if (selectedCell) {
            try {
              const response = await formatExcelContent(
                identity.rawAccessToken, // Access token from MSAL
                mailContent,
                selectedCell,
                activeWorksheet,
                model,
                range
              );
              tableData = parseBackendTableJson(response);
              if (!tableData) {
                logger.warn(
                  "[InsertButton] /insert/excel returned no usable table; falling back to client-side parse"
                );
              }
            } catch (apiErr) {
              logger.warn(
                "[InsertButton] /insert/excel failed; falling back to client-side parse:",
                apiErr
              );
            }
          }

          if (tableData) {
            await insertJsonTableIntoExcel(tableData);
          } else {
            // Fallback: parse a markdown table directly from content, or insert
            // it as plain text. Keeps insertion working if the backend is down or
            // the response is malformed/empty.
            await insertIntoExcel(mailContent);
          }
        } catch (err) {
          logger.error("Failed to insert into Excel:", err);
        }
        return;
      }

      // Handle Outlook context
      if (host === "Outlook") {
        // Only work in Outlook context with mailbox.item available
        if (!Office.context.mailbox?.item) {
          logger.warn("InsertButton: Office.context.mailbox.item not available");
          return;
        }

        const mode = !("itemId" in Office.context.mailbox.item) ? "compose" : "read";

        if (mode === "compose") {
          // In compose mode, insert into draft while preserving quoted section
          try {
            // Get current HTML to find the quoted section
            const currentHtml = await getEmailBody(Office.CoercionType.Html);
            let splitIndex = currentHtml.search(
              /(<div[^>]*class=["']?OutlookMessageHeader|<hr|<div[^>]*class=["']?gmail_quote|From:)/i
            );
            let quotedHtml = splitIndex > -1 ? currentHtml.substring(splitIndex) : "";

            // Convert markdown content to HTML using FastAPI backend
            const rawHtmlReply = await formatContent(
              identity.rawAccessToken, // Access token from MSAL
              mailContent,
              "Outlook",
              `Convert markdown to HTML for Outlook email draft. User's name: ${Office.context.mailbox.userProfile.displayName}`,
              model
            );
            
            const cleanedHtmlReply = formatOutlookBody(rawHtmlReply);
            const newHtml = cleanedHtmlReply + quotedHtml;
            
            // Insert the combined HTML
            await new Promise<void>((resolve, reject) => {
              Office.context.mailbox.item.body.setAsync(
                newHtml,
                { coercionType: Office.CoercionType.Html },
                function (asyncResult) {
                  if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
                    resolve();
                  } else {
                    Office.context.mailbox.item.notificationMessages.addAsync("addTextError", {
                      type: "errorMessage",
                      message: "Failed to update draft: " + asyncResult.error.message
                    });
                    reject(asyncResult.error.message);
                  }
                }
              );
            });
          } catch (err) {
            Office.context.mailbox.item.notificationMessages.addAsync("addTextError", {
              type: "errorMessage",
              message: typeof err === "string" ? err : "Failed to update draft."
            });
          }
        } else if (item && "displayReplyForm" in item) {
          // In read mode, open a reply with the generated content
          const rawHtmlReply = await formatContent(
            identity.rawAccessToken, // Access token from MSAL
            mailContent,
            "Outlook",
            `Convert markdown to HTML for Outlook email reply. User's name: ${Office.context.mailbox.userProfile.displayName}`,
            model
          );
          const cleanedHtmlReply = formatOutlookBody(rawHtmlReply);
          displayReplyForm(item, cleanedHtmlReply);
        } else {
          logger.warn("Item is not in a state that supports replying or composing.");
        }
      } else {
        logger.warn(`InsertButton: Unsupported host type: ${host}`);
      }
    } catch (err) {
      logger.error("Failed to insert:", err);
    } finally {
      setGenerating(false);
      setIsInserting(false);
    }
  };

  // Determine button availability based on host
  const host = Office.context.host as HostType;
  const isOutlook = host === "Outlook";
  const isWord = host === "Word";
  const isPowerPoint = host === "PowerPoint";
  const isExcel = host === "Excel";
  
  // For Outlook: disable if mailbox.item is undefined
  // For Word/PowerPoint/Excel: always available (no mailbox check needed)
  const isItemAvailable = (isWord || isPowerPoint || isExcel) ? true : (isOutlook ? Office.context.mailbox?.item !== undefined : false);

  return (
    <Button 
      variant="ghost"
      size="sm"
      onClick={handleInsert} 
      disabled={disabled || generating || !isItemAvailable}
      title={!isItemAvailable ? "Insert not available in this context" : undefined}
      className="h-6 px-2 text-xs"
    >
      <LuReply className="mr-1 h-3 w-3" />
      {generating ? "Inserting..." : "Insert"}
    </Button>
  );
};